import React, { useLayoutEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { CaseFormDataProvider } from '../components/createCaseForm/CreateCaseContext';
import CreateForm from '../components/createCaseForm/CreateForm';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: 'calc(100vh - 72px)',
    boxSizing: 'border-box',
    padding: '38px 28px 56px',
    background: 'linear-gradient(180deg, #f8fbfc 0%, #eef4f7 100%)',
    color: '#173247',
    fontFamily: 'Arial, Helvetica, sans-serif',

    [theme.breakpoints.down('sm')]: {
      padding: '24px 16px 38px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: 'none',
    margin: 0,
  },

  header: {
    marginBottom: 24,
    padding: '30px 34px',
    borderRadius: 20,
    background: 'linear-gradient(135deg, #163f5b 0%, #0f7289 100%)',
    boxShadow: '0 16px 36px rgba(20, 69, 94, 0.16)',

    [theme.breakpoints.down('sm')]: {
      padding: '24px 22px',
    },
  },

  eyebrow: {
    margin: 0,
    color: 'rgba(255,255,255,0.72)',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },

  title: {
    margin: '6px 0 0',
    color: '#ffffff',
    fontSize: 36,
    lineHeight: 1.15,
    fontWeight: 800,
    letterSpacing: '-0.025em',

    [theme.breakpoints.down('xs')]: {
      fontSize: 29,
    },
  },

  subtitle: {
    margin: '9px 0 0',
    color: 'rgba(255,255,255,0.82)',
    fontSize: 16,
    lineHeight: 1.55,
  },

  // This is the styling already used for the TKR and Choose Preference steps.
  // Leave those steps exactly as they are.
  modernFormCard: {
    minHeight: 430,
    padding: '42px 38px',
    border: '1px solid #dce7ec',
    borderRadius: 20,
    background: '#ffffff',
    boxShadow: '0 16px 38px rgba(18, 61, 82, 0.09)',
    boxSizing: 'border-box',

    '& h1, & h2, & h3, & h4': {
      marginTop: '0 !important',
      fontFamily: 'Arial, Helvetica, sans-serif !important',
      fontWeight: '800 !important',
      letterSpacing: '-0.02em',
    },

    '& h2, & h3, & h4': {
      color: '#173247 !important',
    },

    '& h1': {
      fontSize: '34px !important',
      color: '#ffffff !important',
      WebkitTextFillColor: '#ffffff !important',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
    },

    '& h2': {
      fontSize: '30px !important',
    },

    '& h3': {
      fontSize: '26px !important',
    },

    '& label': {
      color: '#405d70 !important',
      fontSize: '16px !important',
      fontWeight: '700 !important',
    },

    '& input, & select, & textarea': {
      minHeight: 48,
      boxSizing: 'border-box',
      border: '1px solid #cbd9df !important',
      borderRadius: '10px !important',
      background: '#ffffff !important',
      color: '#173247 !important',
      fontSize: '16px !important',
      outline: 'none',
    },

    '& input:focus, & select:focus, & textarea:focus': {
      borderColor: '#109bb9 !important',
      boxShadow: '0 0 0 4px rgba(16, 155, 185, 0.12) !important',
    },

    '& button, & a.btn, & .btn': {
      minHeight: '58px !important',
      padding: '0 30px !important',
      border: '1px solid transparent !important',
      borderRadius: '12px !important',
      background: 'linear-gradient(135deg, #109bb9 0%, #08728c 100%) !important',
      color: '#ffffff !important',
      fontFamily: 'Arial, Helvetica, sans-serif !important',
      fontSize: '19px !important',
      fontWeight: '800 !important',
      lineHeight: '1.2 !important',
      textDecoration: 'none !important',
      textTransform: 'none !important',
      boxShadow: '0 10px 22px rgba(8, 114, 140, 0.20) !important',
      transition: 'transform 140ms ease, box-shadow 140ms ease !important',
    },

    '& button:hover, & a.btn:hover, & .btn:hover': {
      background: 'linear-gradient(135deg, #0d8eaa 0%, #06677f 100%) !important',
      color: '#ffffff !important',
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 26px rgba(8, 114, 140, 0.26) !important',
    },

    '& button *, & a.btn *, & .btn *, & .MuiButton-label, & [role="button"] *': {
      color: '#ffffff !important',
      WebkitTextFillColor: '#ffffff !important',
    },

    '& input[type="button"], & input[type="submit"]': {
      color: '#ffffff !important',
      WebkitTextFillColor: '#ffffff !important',
    },

    '& > div': {
      maxWidth: '100%',
    },

    '& p': {
      color: '#607888',
      fontSize: 16,
      lineHeight: 1.6,
    },

    [theme.breakpoints.down('sm')]: {
      minHeight: 360,
      padding: '30px 20px',
    },
  },

  // IMPORTANT:
  // Final Consent + Preference step uses the original CreateForm UI.
  // No global input/button/table styling is applied here.
  legacyFinalStep: {
    width: '100%',
    maxWidth: '100%',
    minHeight: 0,
    padding: 0,
    margin: 0,
    border: 0,
    borderRadius: 0,
    background: '#ffffff',
    boxShadow: 'none',
    boxSizing: 'border-box',
    overflow: 'visible',
  },
}));

const SurgeonCreateCasePage = () => {
  const classes = useStyles();
  const formRootRef = useRef(null);
  const [isFinalStep, setIsFinalStep] = useState(false);

  useLayoutEffect(() => {
    const root = formRootRef.current;
    if (!root) return undefined;

    const detectStep = () => {
      const text = (root.textContent || '').toLowerCase();

      const finalStep =
        text.includes('consent information')
        && text.includes('preference information');

      setIsFinalStep(finalStep);

      // Remove the Alignment Default Position field from this page.
      const alignmentLabels = Array.from(root.querySelectorAll('label, *')).filter((element) => {
        const ownText = Array.from(element.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent)
          .join('')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();

        return ownText === 'alignment default position*'
          || ownText === 'alignment default position';
      });

      alignmentLabels.forEach((labelElement) => {
        let fieldContainer = labelElement.parentElement;

        while (fieldContainer && fieldContainer !== root) {
          const hasControl = fieldContainer.querySelector('select, input, textarea');
          const controls = fieldContainer.querySelectorAll('select, input, textarea').length;

          if (hasControl && controls <= 2) {
            fieldContainer.style.setProperty('display', 'none', 'important');
            break;
          }

          fieldContainer = fieldContainer.parentElement;
        }
      });

      // Do not touch the final step at all.
      if (finalStep) {
        return;
      }

      const elements = Array.from(root.querySelectorAll('*'));

      const makeWhite = (element) => {
        if (!element) return;

        element.style.setProperty('color', '#ffffff', 'important');
        element.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
        element.style.setProperty('fill', '#ffffff', 'important');

        Array.from(element.querySelectorAll('*')).forEach((child) => {
          child.style.setProperty('color', '#ffffff', 'important');
          child.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
          child.style.setProperty('fill', '#ffffff', 'important');
        });
      };

      // STEP 1 — preserve the white TKR text.
      elements.forEach((element) => {
        const ownText = Array.from(element.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent)
          .join('')
          .trim()
          .toUpperCase();

        if (ownText === 'TKR') {
          makeWhite(element);

          const clickable = element.closest('button, a, [role="button"], .btn');
          if (clickable) {
            makeWhite(clickable);
          }
        }
      });

      // STEP 2 — preserve the Knee 1 / Knee 2 preference-card appearance.
      const requiredLabels = [
        'distributor name',
        'hospital name',
        'mri center name',
        'implant name'
      ];

      const labelElements = elements.filter((element) => {
        const value = (element.textContent || '').trim().toLowerCase();
        return value === 'distributor name:' || value === 'distributor name';
      });

      labelElements.forEach((labelElement) => {
        let card = labelElement.parentElement;

        while (card && card !== root) {
          const cardText = (card.textContent || '').toLowerCase();

          const hasAllPreferenceFields = requiredLabels.every((label) =>
            cardText.includes(label)
          );

          const hasKneeTitle = cardText.includes('knee');

          if (hasAllPreferenceFields && hasKneeTitle) {
            makeWhite(card);

            card.style.setProperty(
              'font-family',
              'Arial, Helvetica, sans-serif',
              'important'
            );

            Array.from(card.querySelectorAll('*')).forEach((child) => {
              child.style.setProperty(
                'font-family',
                'Arial, Helvetica, sans-serif',
                'important'
              );
              child.style.setProperty('font-style', 'normal', 'important');
              child.style.setProperty('letter-spacing', 'normal', 'important');
              child.style.setProperty('text-transform', 'none', 'important');
              child.style.setProperty('line-height', '1.35', 'important');

            });

            break;
          }

          card = card.parentElement;
        }
      });
    };

    detectStep();

    const observer = new MutationObserver(detectStep);
    observer.observe(root, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return (
    <main className={classes.page}>
      <div className={classes.shell}>
        <section className={classes.header}>
          <p className={classes.eyebrow}>Surgeon Portal</p>
          <h1 className={classes.title}>Create New Order</h1>
          <p className={classes.subtitle}>
            Select the surgery type to begin creating a new patient order.
          </p>
        </section>

        <section
          ref={formRootRef}
          className={
            isFinalStep
              ? classes.legacyFinalStep
              : classes.modernFormCard
          }
        >
          <CaseFormDataProvider>
            <CreateForm />
          </CaseFormDataProvider>
        </section>
      </div>
    </main>
  );
};

export default SurgeonCreateCasePage;